import React, { useEffect, useRef, useState } from 'react';
import {
  Check,
  Copy,
  Loader2,
} from 'lucide-react';
import { ThemeProvider } from './theme';
import { Footer, Header, ThemeToggle } from './layout';
import './theme/globals.css';

type TaskType = 'general' | 'code' | 'math' | 'analysis' | 'writing';
type RoutingMode = 'budget' | 'balanced' | 'quality';

type PlaygroundCandidate = {
  rank: number;
  model: string;
  predictedScore: number;
  estimatedCostUsd: number;
  predictedScoreStd?: number;
  estimatedCostStdUsd?: number;
  routerUncertaintyStd?: number;
  routerCostUncertaintyStdUsd?: number;
};

type PlaygroundResult = {
  task: TaskType;
  mode: RoutingMode;
  selected: PlaygroundCandidate;
  candidates: PlaygroundCandidate[];
  completion?: string;
  dryRun?: boolean;
};

type PlaygroundHealth = {
  ok: boolean;
  runsEnabled: boolean;
  inviteRequired: boolean;
  dryRun: boolean;
  promptMaxChars: number;
};

const API_BASE = process.env.REACT_APP_PLAYGROUND_API_URL || '';

const SAMPLE_PROMPTS = [
  {
    label: 'Constrained code',
    text: 'In six lines of pytest, show a cache stampede and assert that only one worker reaches the upstream API.',
  },
  {
    label: 'Research synthesis',
    text: 'Compare three plausible explanations for why a model router can beat the best fixed model, then identify the experiment that would distinguish them.',
  },
  {
    label: 'Strict extraction',
    text: 'Extract every deadline, owner, and unresolved dependency from these meeting notes as valid JSON. Do not infer missing values.',
  },
];

const PUBLIC_MODEL_POOL: PlaygroundCandidate[] = [
  {
    rank: 1,
    model: 'Qwen3 235B A22B 2507',
    predictedScore: 0.7023,
    estimatedCostUsd: 0.000055,
  },
  {
    rank: 2,
    model: 'DeepSeek V4 Flash',
    predictedScore: 0.6997,
    estimatedCostUsd: 0.000148,
  },
  {
    rank: 3,
    model: 'Gemini 3.1 Flash-Lite',
    predictedScore: 0.7297,
    estimatedCostUsd: 0.00078,
  },
  {
    rank: 4,
    model: 'Qwen3 Next 80B A3B',
    predictedScore: 0.6787,
    estimatedCostUsd: 0.000565,
  },
  {
    rank: 5,
    model: 'Qwen3 Coder Next',
    predictedScore: 0.6734,
    estimatedCostUsd: 0.000411,
  },
  {
    rank: 6,
    model: 'GPT-5.4 Mini',
    predictedScore: 0.6574,
    estimatedCostUsd: 0.00235,
  },
];

const DEFAULT_PROMPT =
  'LoopOne\'s tiny router rolls its eyes at "just use the smartest model." In six lines of pytest, show the winner switching when cost sensitivity changes.';

function formatUsd(value: number) {
  return `$${value.toFixed(value < 0.001 ? 5 : 4)}`;
}

function formatScore(value: number) {
  return value <= 1 ? value.toFixed(3) : value.toFixed(1);
}

function costLambdaForTradeoff(value: number) {
  const position = Math.max(0, Math.min(100, value)) / 100;
  if (position <= 0.5) {
    return Math.exp(Math.log(800) + (position / 0.5) * (Math.log(250) - Math.log(800)));
  }
  const qualityPosition = (position - 0.5) / 0.5;
  return 250 * Math.pow(1 - qualityPosition, 3);
}

function modeForTradeoff(value: number): RoutingMode {
  if (value < 34) return 'budget';
  if (value > 66) return 'quality';
  return 'balanced';
}

function candidateUtility(candidate: PlaygroundCandidate, tradeoff: number) {
  return candidate.predictedScore - costLambdaForTradeoff(tradeoff) * candidate.estimatedCostUsd;
}

function rankForTradeoff(candidates: PlaygroundCandidate[], value: number) {
  return [...candidates]
    .sort((left, right) => {
      const leftUtility = candidateUtility(left, value);
      const rightUtility = candidateUtility(right, value);
      return rightUtility - leftUtility;
    })
    .map((candidate, index) => ({ ...candidate, rank: index + 1 }));
}

function friendlyError(status: number, detail: string) {
  if (status === 401) return detail || 'Live runs are not available for this session.';
  if (status === 429) return detail || 'The playground limit has been reached. Try again later.';
  if (status === 503) return detail || 'Live runs are temporarily disabled.';
  if (status >= 500) return detail || 'The backend could not complete this request.';
  return detail || `Backend returned HTTP ${status}.`;
}

type ResponseBlock =
  | { type: 'heading'; text: string; level: number }
  | { type: 'paragraph'; text: string }
  | { type: 'list'; ordered: boolean; items: string[] }
  | { type: 'math'; expression: string }
  | { type: 'code'; language: string; code: string };

function cleanMarkdownText(value: string) {
  return value
    .replace(/\*\*(.*?)\*\*/g, '$1')
    .replace(/__(.*?)__/g, '$1')
    .trim();
}

function isPythonLikeStart(line: string) {
  return /^\s*(from\s+\S+\s+import|import\s+\S+|def\s+\w+|async\s+def\s+\w+|class\s+\w+|@[\w.]+|if\s+__name__\s*==)/.test(line);
}

function isPythonLikeContinuation(line: string) {
  return line.trim() === '' ||
    /^\s{2,}\S/.test(line) ||
    /^\s*(#|return\b|yield\b|raise\b|pass\b|break\b|continue\b|else:|elif\b|except\b|finally:)/.test(line);
}

function parseResponseBlocks(text: string): ResponseBlock[] {
  const lines = text.replace(/\r\n/g, '\n').trim().split('\n');
  const blocks: ResponseBlock[] = [];
  let paragraphLines: string[] = [];
  let listItems: string[] = [];
  let listOrdered = false;

  const flushParagraph = () => {
    if (!paragraphLines.length) return;
    const textValue = cleanMarkdownText(paragraphLines.join(' '));
    if (textValue) blocks.push({ type: 'paragraph', text: textValue });
    paragraphLines = [];
  };

  const flushList = () => {
    if (!listItems.length) return;
    blocks.push({ type: 'list', ordered: listOrdered, items: listItems.map(cleanMarkdownText).filter(Boolean) });
    listItems = [];
  };

  const flushText = () => {
    flushParagraph();
    flushList();
  };

  for (let index = 0; index < lines.length; index += 1) {
    const line = lines[index];
    const trimmed = line.trim();
    const fence = trimmed.match(/^```([A-Za-z0-9_+.-]*)\s*$/);

    if (fence) {
      flushText();
      const language = fence[1] || 'code';
      const codeLines: string[] = [];
      index += 1;
      while (index < lines.length && !lines[index].trim().startsWith('```')) {
        codeLines.push(lines[index]);
        index += 1;
      }
      blocks.push({ type: 'code', language, code: codeLines.join('\n').trimEnd() });
      continue;
    }

    if (isPythonLikeStart(line)) {
      flushText();
      const codeLines = [line];
      while (index + 1 < lines.length && isPythonLikeContinuation(lines[index + 1])) {
        index += 1;
        codeLines.push(lines[index]);
      }
      blocks.push({ type: 'code', language: 'python', code: codeLines.join('\n').trimEnd() });
      continue;
    }

    if (!trimmed) {
      flushText();
      continue;
    }

    const displayMath = trimmed.match(/^\$\$\s*(.+?)\s*\$\$$/) || trimmed.match(/^\\\[\s*(.+?)\s*\\\]$/);
    if (displayMath) {
      flushText();
      blocks.push({ type: 'math', expression: displayMath[1] });
      continue;
    }

    if (/\\(?:dfrac|tfrac|frac|boxed|fbox|sqrt|text|mathrm|times|cdot|div|leq|geq|neq|approx)\b/.test(trimmed)) {
      flushText();
      blocks.push({ type: 'math', expression: trimmed });
      continue;
    }

    const heading = trimmed.match(/^(#{1,4})\s+(.+)/);
    if (heading) {
      flushText();
      blocks.push({ type: 'heading', level: heading[1].length, text: cleanMarkdownText(heading[2]) });
      continue;
    }

    const bullet = trimmed.match(/^[-*]\s+(.+)/);
    const ordered = trimmed.match(/^\d+[.)]\s+(.+)/);
    if (bullet || ordered) {
      flushParagraph();
      const nextOrdered = Boolean(ordered);
      if (listItems.length && listOrdered !== nextOrdered) flushList();
      listOrdered = nextOrdered;
      listItems.push((bullet || ordered)?.[1] || '');
      continue;
    }

    flushList();
    paragraphLines.push(line);
  }

  flushText();
  return blocks;
}

function renderPlainMathSegment(value: string) {
  return value
    .replace(/\\left|\\right/g, '')
    .replace(/\\(?:text|mathrm)\{([^}]*)\}/g, '$1')
    .replace(/\\(?:boxed|fbox)\{([^{}]*)\}/g, '$1')
    .replace(/\\times/g, '×')
    .replace(/\\cdot/g, '·')
    .replace(/\\div/g, '÷')
    .replace(/\\leq/g, '≤')
    .replace(/\\geq/g, '≥')
    .replace(/\\neq/g, '≠')
    .replace(/\\approx/g, '≈')
    .replace(/\\[A-Za-z]+\s*/g, '')
    .replace(/[{}]/g, '')
    .replace(/\s+/g, ' ')
    .trim();
}

function MathText({ expression, display = false }: { expression: string; display?: boolean }) {
  const parts: React.ReactNode[] = [];
  const normalizedExpression = expression
    .replace(/\\(?:text|mathrm)\{([^}]*)\}/g, '$1')
    .replace(/\\(?:boxed|fbox)\{([^{}]*)\}/g, '$1');
  const pattern = /\\(?:dfrac|tfrac|frac)\{([^{}]+)\}\{([^{}]+)\}/g;
  let cursor = 0;
  let match: RegExpExecArray | null;

  while ((match = pattern.exec(normalizedExpression)) !== null) {
    const before = renderPlainMathSegment(normalizedExpression.slice(cursor, match.index));
    if (before) parts.push(<span key={`text-${cursor}`}>{before}</span>);
    parts.push(
      <span key={`frac-${match.index}`} className="inline-grid translate-y-[0.18em] grid-rows-[auto_auto] px-1 text-center font-mono leading-none">
        <span className="border-b border-current px-1 pb-0.5">{renderPlainMathSegment(match[1])}</span>
        <span className="px-1 pt-0.5">{renderPlainMathSegment(match[2])}</span>
      </span>
    );
    cursor = match.index + match[0].length;
  }

  const after = renderPlainMathSegment(normalizedExpression.slice(cursor));
  if (after) parts.push(<span key={`text-${cursor}`}>{after}</span>);
  const content = parts.length ? parts : renderPlainMathSegment(normalizedExpression);

  if (display) {
    return (
      <div className="overflow-x-auto rounded-2xl border border-border bg-background/75 px-4 py-3 font-mono text-body text-foreground dark:bg-white/[0.03]">
        {content}
      </div>
    );
  }

  return (
    <span className="rounded-md border border-border bg-foreground/[0.05] px-1.5 py-0.5 font-mono text-[0.9em] text-foreground">
      {content}
    </span>
  );
}

function InlineText({ text }: { text: string }) {
  const parts = cleanMarkdownText(text).split(/(`[^`]+`|\$\$[^$]+\$\$|\\\([^)]*\\\)|\\\[[\s\S]*?\\\])/g);
  return (
    <>
      {parts.map((part, index) => {
        if (part.startsWith('`') && part.endsWith('`')) {
          return (
            <code key={`${part}-${index}`} className="rounded-md border border-border bg-foreground/[0.05] px-1.5 py-0.5 font-mono text-[0.9em] text-foreground">
              {part.slice(1, -1)}
            </code>
          );
        }
        if (part.startsWith('$$') && part.endsWith('$$')) {
          return <MathText key={`${part}-${index}`} expression={part.slice(2, -2)} />;
        }
        if (part.startsWith('\\(') && part.endsWith('\\)')) {
          return <MathText key={`${part}-${index}`} expression={part.slice(2, -2)} />;
        }
        if (part.startsWith('\\[') && part.endsWith('\\]')) {
          return <MathText key={`${part}-${index}`} expression={part.slice(2, -2)} />;
        }
        return <React.Fragment key={`${part}-${index}`}>{part}</React.Fragment>;
      })}
    </>
  );
}

function CodeBlock({ code, language }: { code: string; language: string }) {
  return (
    <div className="overflow-hidden rounded-2xl border border-border bg-[hsl(220,18%,10%)] shadow-[0_18px_60px_-45px_rgba(0,0,0,0.6)]">
      <div className="flex items-center justify-between border-b border-white/10 px-3 py-2 sm:px-4">
        <span className="font-mono text-[11px] uppercase tracking-[0.16em] text-white/55">
          {language || 'code'}
        </span>
      </div>
      <pre className="overflow-x-auto whitespace-pre-wrap break-normal p-3 text-[10.5px] leading-[1.55] text-white/90 [tab-size:2] sm:whitespace-pre sm:p-4 sm:text-[13px] sm:leading-6">
        <code className="font-mono">{code || ' '}</code>
      </pre>
    </div>
  );
}

function FormattedResponse({ text }: { text: string }) {
  const blocks = parseResponseBlocks(text);

  if (!blocks.length) return null;

  return (
    <div className="space-y-5">
      {blocks.map((block, index) => {
        if (block.type === 'heading') {
          const isPrimary = block.level <= 2;
          return (
            <h3
              key={`${block.type}-${index}`}
              className={[
                'font-display tracking-normal text-foreground',
                isPrimary ? 'pt-2 text-[22px] leading-[28px]' : 'pt-1 text-[19px] leading-[25px]',
              ].join(' ')}
            >
              {block.text}
            </h3>
          );
        }

        if (block.type === 'list') {
          const ListTag = block.ordered ? 'ol' : 'ul';
          return (
            <ListTag
              key={`${block.type}-${index}`}
              className={[
                'space-y-2 text-body leading-7 text-foreground-secondary',
                block.ordered ? 'list-decimal pl-6' : 'list-disc pl-6',
              ].join(' ')}
            >
              {block.items.map((item, itemIndex) => (
                <li key={`${item}-${itemIndex}`} className="pl-1">
                  <InlineText text={item} />
                </li>
              ))}
            </ListTag>
          );
        }

        if (block.type === 'code') {
          return <CodeBlock key={`${block.type}-${index}`} code={block.code} language={block.language} />;
        }

        if (block.type === 'math') {
          return <MathText key={`${block.type}-${index}`} expression={block.expression} display />;
        }

        return (
          <p key={`${block.type}-${index}`} className="text-body leading-7 text-foreground-secondary">
            <InlineText text={block.text} />
          </p>
        );
      })}
    </div>
  );
}

function StatusPill({ health }: { health: PlaygroundHealth | null }) {
  const label = !health
    ? 'Connecting'
    : health.dryRun
      ? 'Preview data'
      : !health.runsEnabled
        ? 'Routing paused'
        : 'Live';
  const isLive = label === 'Live';

  return (
    <span className="inline-flex items-center gap-2 font-mono text-[11px] uppercase tracking-[0.18em] text-foreground-secondary">
      <span className="relative flex h-2 w-2" aria-hidden="true">
        {isLive && <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-brand-mint/55 [animation-duration:2.4s]" />}
        <span className={`relative inline-flex h-2 w-2 rounded-full ${isLive ? 'bg-brand-mint' : 'bg-foreground-muted/50'}`} />
      </span>
      {label}
    </span>
  );
}

function shortModelName(model: string) {
  return model
    .replace('Qwen3 235B A22B 2507', 'Qwen3 235B')
    .replace('Qwen3 Next 80B A3B', 'Qwen3 Next 80B')
    .replace('Qwen3 Coder Next', 'Qwen3 Coder')
    .replace('DeepSeek V4 Flash', 'DeepSeek V4')
    .replace('GPT-5.4 Mini', 'GPT-5.4 Mini');
}

function starPoints(cx: number, cy: number, outerRadius = 11, innerRadius = 4.8) {
  return Array.from({ length: 10 }, (_, index) => {
    const radius = index % 2 === 0 ? outerRadius : innerRadius;
    const angle = -Math.PI / 2 + (index * Math.PI) / 5;
    return `${cx + Math.cos(angle) * radius},${cy + Math.sin(angle) * radius}`;
  }).join(' ');
}

function CandidatePlane({
  candidates,
  selected,
  activeModel,
  tradeoff,
  ready,
  onActiveModel,
}: {
  candidates: PlaygroundCandidate[];
  selected: PlaygroundCandidate | null;
  activeModel: string | null;
  tradeoff: number;
  ready: boolean;
  onActiveModel: (model: string | null) => void;
}) {
  const width = 640;
  const height = 366;
  const left = 74;
  const right = 612;
  const top = 34;
  const bottom = 306;
  const costs = candidates.map(candidate => Math.max(candidate.estimatedCostUsd, 0.000001));
  const costLows = candidates.map(candidate => Math.max(
    candidate.estimatedCostUsd - Math.max(candidate.estimatedCostStdUsd || 0, candidate.routerCostUncertaintyStdUsd || 0),
    0.000001,
  ));
  const costHighs = candidates.map(candidate => (
    candidate.estimatedCostUsd + Math.max(candidate.estimatedCostStdUsd || 0, candidate.routerCostUncertaintyStdUsd || 0)
  ));
  const minCost = Math.min(...costs, ...costLows) * 0.72;
  const maxCost = Math.max(...costs, ...costHighs) * 1.38;
  const minScore = 0;
  const maxScore = 1;
  const x = (value: number) => left + ((Math.log(value) - Math.log(minCost)) / (Math.log(maxCost) - Math.log(minCost))) * (right - left);
  const y = (value: number) => bottom - ((value - minScore) / (maxScore - minScore)) * (bottom - top);
  const clamp = (value: number, minimum: number, maximum: number) => Math.max(minimum, Math.min(maximum, value));
  const scoreTicks = [0, 0.5, 1];
  const costTicks = [minCost, Math.sqrt(minCost * maxCost), maxCost];
  const costLambda = costLambdaForTradeoff(tradeoff);
  const selectedUtility = selected ? candidateUtility(selected, tradeoff) : null;
  const objectivePath = selectedUtility === null
    ? ''
    : Array.from({ length: 48 }, (_, index) => {
        const fraction = index / 47;
        const cost = Math.exp(Math.log(minCost) + fraction * (Math.log(maxCost) - Math.log(minCost)));
        const score = selectedUtility + costLambda * cost;
        return `${index === 0 ? 'M' : 'L'} ${x(cost).toFixed(2)} ${y(score).toFixed(2)}`;
      }).join(' ');

  return (
    <div>
      <div className="flex items-center justify-between px-2 pb-1 font-mono text-[10px] uppercase tracking-[0.12em] text-foreground-muted sm:hidden">
        <span>↑ Accuracy</span>
        <span>Cost →</span>
      </div>
      <svg viewBox={`0 0 ${width} ${height}`} className="block h-auto w-full" role="img" aria-label="Predicted accuracy and estimated cost for the candidate model pool">
      <defs>
        <clipPath id="router-outcome-plane-clip">
          <rect x={left} y={top} width={right - left} height={bottom - top} rx="8" />
        </clipPath>
        <filter id="router-uncertainty-soften" x="-35%" y="-35%" width="170%" height="170%">
          <feGaussianBlur stdDeviation="3.5" />
        </filter>
      </defs>
      {scoreTicks.map(tick => (
        <g key={tick}>
          <line x1={left} x2={right} y1={y(tick)} y2={y(tick)} className="stroke-border" strokeWidth="1" />
          <text x={left - 13} y={y(tick) + 5} textAnchor="end" className="hidden fill-foreground-muted font-mono text-[12px] sm:block">
            {tick.toFixed(2)}
          </text>
        </g>
      ))}
      {costTicks.map(tick => (
        <g key={tick}>
          <line x1={x(tick)} x2={x(tick)} y1={top} y2={bottom} className="stroke-border" strokeWidth="1" strokeDasharray="2 6" />
          <text x={x(tick)} y={bottom + 24} textAnchor="middle" className="hidden fill-foreground-muted font-mono text-[12px] sm:block">
            {formatUsd(tick)}
          </text>
        </g>
      ))}
      <text
        x="18"
        y={(top + bottom) / 2}
        transform={`rotate(-90 18 ${(top + bottom) / 2})`}
        textAnchor="middle"
        className="hidden fill-foreground-muted font-mono text-[9px] uppercase tracking-[0.14em] sm:block"
      >
        Predicted accuracy
      </text>
      <text x={right} y={height - 5} textAnchor="end" className="hidden fill-foreground-muted font-mono text-[9px] uppercase tracking-[0.14em] sm:block">
        Estimated cost / request
      </text>

      {objectivePath && (
        <path
          d={objectivePath}
          clipPath="url(#router-outcome-plane-clip)"
          className="fill-none stroke-brand-amber/65 transition-all duration-300"
          strokeWidth="1.5"
          strokeDasharray="5 7"
        />
      )}

      {candidates.map(candidate => {
        const isSelected = selected?.model === candidate.model;
        const isActive = activeModel === candidate.model;
        const cx = x(Math.max(candidate.estimatedCostUsd, 0.000001));
        const cy = y(candidate.predictedScore);
        const showLabel = ready && (isSelected || isActive);
        const labelOnLeft = cx > right - 150;
        const scoreStd = candidate.predictedScoreStd || 0;
        const costStd = candidate.estimatedCostStdUsd || 0;
        const routerStd = candidate.routerUncertaintyStd || 0;
        const routerCostStd = candidate.routerCostUncertaintyStdUsd || 0;
        const costLow = Math.max(candidate.estimatedCostUsd - costStd, minCost);
        const costHigh = Math.min(candidate.estimatedCostUsd + costStd, maxCost);
        const routerCostLow = Math.max(candidate.estimatedCostUsd - routerCostStd, minCost);
        const routerCostHigh = Math.min(candidate.estimatedCostUsd + routerCostStd, maxCost);
        const rawResponseRx = Math.abs(x(costHigh) - x(costLow)) / 2;
        const scoreLow = clamp(candidate.predictedScore - scoreStd, minScore, maxScore);
        const scoreHigh = clamp(candidate.predictedScore + scoreStd, minScore, maxScore);
        const routerLow = clamp(candidate.predictedScore - routerStd, minScore, maxScore);
        const routerHigh = clamp(candidate.predictedScore + routerStd, minScore, maxScore);
        const rawResponseRy = Math.abs(y(scoreHigh) - y(scoreLow)) / 2;
        const responseRx = costStd > 0 ? clamp(rawResponseRx, 8, 28) : 0;
        const responseRy = scoreStd > 0 ? clamp(rawResponseRy, 10, 34) : 0;
        const routerRxMinimum = routerCostStd > costStd ? responseRx + 5 : responseRx;
        const routerRyMinimum = routerStd > scoreStd ? responseRy + 5 : responseRy;
        const routerRx = routerCostStd > 0
          ? clamp(Math.abs(x(routerCostHigh) - x(routerCostLow)) / 2, routerRxMinimum, 38)
          : 0;
        const routerRy = routerStd > 0
          ? clamp(Math.abs(y(routerHigh) - y(routerLow)) / 2, routerRyMinimum, 46)
          : 0;
        const hasUncertainty = scoreStd > 0 || costStd > 0 || routerStd > 0 || routerCostStd > 0;
        const uncertaintyTitle = hasUncertainty
          ? `Run-to-run variation ±${scoreStd.toFixed(3)}; router uncertainty ±${routerStd.toFixed(3)}`
          : 'Uncertainty is not available for this prediction';
        return (
          <g
            key={candidate.model}
            tabIndex={0}
            className="cursor-default outline-none"
            onMouseEnter={() => onActiveModel(candidate.model)}
            onMouseLeave={() => onActiveModel(null)}
            onFocus={() => onActiveModel(candidate.model)}
            onBlur={() => onActiveModel(null)}
          >
            <title>{`${candidate.model}: ${formatScore(candidate.predictedScore)} predicted accuracy at ${formatUsd(candidate.estimatedCostUsd)}. ${uncertaintyTitle}.`}</title>
            {ready && hasUncertainty && (
              <g clipPath="url(#router-outcome-plane-clip)" className="transition-opacity duration-200">
                <ellipse
                  cx={cx}
                  cy={cy}
                  rx={responseRx * 1.12}
                  ry={responseRy * 1.12}
                  className="fill-brand-pink"
                  fillOpacity={isSelected ? 0.16 : isActive ? 0.13 : 0.075}
                  filter="url(#router-uncertainty-soften)"
                />
                <ellipse
                  cx={cx}
                  cy={cy}
                  rx={responseRx}
                  ry={responseRy}
                  className="fill-brand-pink stroke-brand-pink"
                  fillOpacity={isSelected ? 0.13 : isActive ? 0.10 : 0.055}
                  strokeOpacity={isSelected ? 0.62 : isActive ? 0.5 : 0.28}
                  strokeWidth={isSelected ? 1.6 : 1.15}
                />
                <ellipse
                  cx={cx}
                  cy={cy}
                  rx={routerRx}
                  ry={routerRy}
                  fill="none"
                  className="stroke-brand-pink"
                  strokeOpacity={isSelected ? 0.72 : isActive ? 0.58 : 0.34}
                  strokeWidth={isSelected ? 1.5 : 1.1}
                  strokeDasharray="3 5"
                />
              </g>
            )}
            {(isSelected || isActive) && (
              <circle
                cx={cx}
                cy={cy}
                r={isSelected ? 18 : 13}
                fill="none"
                fillOpacity={0}
                className={isSelected ? 'stroke-brand-amber' : 'stroke-foreground'}
                strokeOpacity={isSelected ? 0.36 : 0.18}
                strokeWidth={isSelected ? 1.8 : 1.2}
              />
            )}
            {isSelected ? (
              <polygon
                points={starPoints(cx, cy)}
                className="fill-brand-amber stroke-background transition-all duration-300"
                strokeWidth="3"
              />
            ) : (
              <circle
                cx={cx}
                cy={cy}
                r={isActive ? 6 : 4.5}
                className="fill-background stroke-foreground-secondary transition-all duration-200"
                strokeWidth={isActive ? 2.5 : 1.6}
                opacity={ready ? 1 : 0.35}
              />
            )}
            {showLabel && (
              <text
                x={labelOnLeft ? cx - 16 : cx + 16}
                y={cy - 13}
                textAnchor={labelOnLeft ? 'end' : 'start'}
                className="fill-foreground font-mono text-[24px] font-medium sm:text-[13px]"
              >
                {shortModelName(candidate.model)}
              </text>
            )}
          </g>
        );
      })}
      </svg>
    </div>
  );
}

function AnimatedRanking({
  candidates,
  selected,
  ready,
  activeModel,
  onActiveModel,
}: {
  candidates: PlaygroundCandidate[];
  selected: PlaygroundCandidate | null;
  ready: boolean;
  activeModel: string | null;
  onActiveModel: (model: string | null) => void;
}) {
  const rowHeight = 70;
  const stableCandidates = [...candidates].sort((left, right) => left.model.localeCompare(right.model));

  return (
    <div className="relative" style={{ height: `${stableCandidates.length * rowHeight}px` }}>
      {stableCandidates.map(candidate => {
        const isSelected = selected?.model === candidate.model;
        const isActive = activeModel === candidate.model;
        return (
          <div
            key={candidate.model}
            className={[
              'absolute inset-x-0 top-0 grid min-h-[58px] grid-cols-[32px_minmax(0,1fr)_auto] items-center gap-3 rounded-xl border px-3 py-2.5 transition-[transform,border-color,background-color,opacity] duration-300 ease-out',
              isSelected
                ? 'border-brand-amber/55 bg-brand-amber/[0.08]'
                : isActive
                  ? 'border-foreground/25 bg-foreground/[0.04]'
                  : 'border-transparent bg-transparent',
              ready ? 'opacity-100' : 'opacity-45',
            ].join(' ')}
            style={{ transform: `translateY(${(candidate.rank - 1) * rowHeight}px)` }}
            onMouseEnter={() => onActiveModel(candidate.model)}
            onMouseLeave={() => onActiveModel(null)}
          >
            <span className={`font-mono text-[12px] ${isSelected ? 'text-brand-amber' : 'text-foreground-muted'}`}>
              {ready ? String(candidate.rank).padStart(2, '0') : '·'}
            </span>
            <div className="min-w-0">
              <div className="flex min-w-0 items-center gap-2">
                {isSelected && <span aria-hidden="true" className="text-[13px] leading-none text-brand-amber">★</span>}
                <span className="truncate text-[14px] text-foreground">{shortModelName(candidate.model)}</span>
              </div>
              <div className="mt-1 flex gap-3 font-mono text-[11px] text-foreground-muted">
                <span>accuracy {formatScore(candidate.predictedScore)}</span>
                <span>{formatUsd(candidate.estimatedCostUsd * 1000)} / 1k</span>
              </div>
            </div>
            {candidate.predictedScoreStd !== undefined && (
              <span
                className="font-mono text-[10px] text-brand-pink"
                title="Run-to-run variation"
              >
                ±{candidate.predictedScoreStd.toFixed(3)}
              </span>
            )}
          </div>
        );
      })}
    </div>
  );
}

function RouterPlaygroundContent({ embedded = false }: { embedded?: boolean }) {
  const [prompt, setPrompt] = useState(DEFAULT_PROMPT);
  const [tradeoff, setTradeoff] = useState(50);
  const [result, setResult] = useState<PlaygroundResult | null>(null);
  const [health, setHealth] = useState<PlaygroundHealth | null>(null);
  const [hasRun, setHasRun] = useState(false);
  const [copied, setCopied] = useState(false);
  const [isRunning, setIsRunning] = useState(false);
  const [isPreviewing, setIsPreviewing] = useState(false);
  const [runStage, setRunStage] = useState('');
  const [showDelayHint, setShowDelayHint] = useState(false);
  const [activeModel, setActiveModel] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const hasAutoRouted = useRef(false);

  const isRequestPending = isPreviewing || isRunning;
  const hasScored = Boolean(result);
  const sourceCandidates = result?.candidates || PUBLIC_MODEL_POOL;
  const candidates = result ? rankForTradeoff(sourceCandidates, tradeoff) : sourceCandidates;
  const selected = result ? candidates[0] || null : null;
  const answer = hasRun ? result?.completion || '' : '';
  const promptLimit = health?.promptMaxChars || 2000;
  const showGenerationPanel = isRunning || hasRun;

  const refreshHealth = async () => {
    try {
      const response = await fetch(`${API_BASE}/api/playground/health`);
      if (response.ok) {
        setHealth((await response.json()) as PlaygroundHealth);
      }
    } catch {
      setHealth(null);
    }
  };

  useEffect(() => {
    refreshHealth();
  }, []);

  useEffect(() => {
    if (!isRequestPending) {
      setShowDelayHint(false);
      return;
    }

    const timer = window.setTimeout(() => setShowDelayHint(true), 4500);
    return () => window.clearTimeout(timer);
  }, [isRequestPending]);

  const resetRouteState = () => {
    setResult(null);
    setHasRun(false);
    setCopied(false);
    setShowDelayHint(false);
    setActiveModel(null);
    setError(null);
  };

  const previewRoute = async () => {
    if (!prompt.trim()) return;
    setIsPreviewing(true);
    setError(null);
    setRunStage('Ranking the model pool for this query.');
    try {
      const response = await fetch(`${API_BASE}/api/playground/route`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ prompt, mode: modeForTradeoff(tradeoff) }),
      });
      const body = await response.json().catch(() => ({}));
      if (!response.ok) {
        throw new Error(friendlyError(response.status, body.detail));
      }
      setResult(body as PlaygroundResult);
      setHasRun(false);
      refreshHealth();
    } catch (routeError) {
      setError(routeError instanceof Error ? routeError.message : 'Route preview failed.');
    } finally {
      setIsPreviewing(false);
      setRunStage('');
    }
  };

  useEffect(() => {
    if (embedded || hasAutoRouted.current) return;
    hasAutoRouted.current = true;
    const timer = window.setTimeout(() => {
      void previewRoute();
    }, 120);
    return () => window.clearTimeout(timer);
    // The standalone lab deliberately makes one prediction on arrival. Editing
    // the request still requires an explicit refresh.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [embedded]);

  const runRouter = async () => {
    if (!prompt.trim()) return;

    setIsRunning(true);
    setRunStage('Generating response.');
    setError(null);
    setCopied(false);

    try {
      const response = await fetch(`${API_BASE}/api/playground/run`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ prompt, mode: modeForTradeoff(tradeoff) }),
      });
      const body = await response.json().catch(() => ({}));
      if (!response.ok) {
        throw new Error(friendlyError(response.status, body.detail));
      }
      setResult(body as PlaygroundResult);
      setHasRun(true);
      refreshHealth();
    } catch (runError) {
      setError(runError instanceof Error ? runError.message : 'Backend request failed.');
    } finally {
      setIsRunning(false);
      setRunStage('');
    }
  };

  const copyResponse = async () => {
    try {
      await navigator.clipboard.writeText(answer);
      setCopied(true);
      window.setTimeout(() => setCopied(false), 1600);
    } catch {
      setCopied(false);
    }
  };

  if (embedded) {
    const embeddedCandidates = rankForTradeoff(result?.candidates ?? [], tradeoff);
    const embeddedSelected = embeddedCandidates[0] ?? null;

    return (
      <section className="overflow-hidden rounded-[20px] border border-border bg-background text-foreground">
        <div className="border-b border-border px-5 py-4 sm:px-7">
          <span className="font-mono text-[11px] uppercase tracking-[0.18em] text-foreground-muted">Router demo</span>
        </div>

        <div className="border-b border-border p-5 sm:p-7">
          <label htmlFor="router-blog-prompt" className="font-mono text-[11px] uppercase tracking-[0.18em] text-foreground-muted">
            Request
          </label>
          <textarea
            id="router-blog-prompt"
            value={prompt}
            onChange={event => {
              setPrompt(event.target.value);
              resetRouteState();
            }}
            maxLength={promptLimit}
            className="mt-3 min-h-[126px] w-full resize-y border-0 bg-transparent p-0 font-body text-[16px] leading-7 text-foreground outline-none placeholder:text-foreground-muted"
            placeholder="Give the router a request."
          />
          <div className="mt-3 flex items-center justify-end border-t border-border pt-3">
            <span className="font-mono text-[10px] text-foreground-muted">{prompt.length}/{promptLimit}</span>
          </div>

          <div className="mt-6">
            <div className="flex items-center justify-between gap-4">
              <span className="font-mono text-[11px] uppercase tracking-[0.18em] text-foreground-muted">Lower cost</span>
              <span className="font-mono text-[11px] uppercase tracking-[0.18em] text-foreground-muted">Higher quality</span>
            </div>
            <div className="relative mt-3 h-8">
              <div className="absolute inset-x-0 top-1/2 h-1 -translate-y-1/2 rounded-full bg-foreground/15" />
              <div
                className="absolute left-0 top-1/2 h-1 -translate-y-1/2 rounded-full bg-foreground/55"
                style={{ width: `${tradeoff}%` }}
              />
              <input
                type="range"
                min={0}
                max={100}
                step={1}
                value={tradeoff}
                onChange={event => setTradeoff(Number(event.target.value))}
                aria-label="Trade off predicted quality against estimated cost"
                aria-valuetext={`${tradeoff}% toward predicted quality`}
                className="absolute inset-0 z-20 h-8 w-full cursor-pointer opacity-0"
              />
              <span
                className="pointer-events-none absolute top-1/2 h-5 w-5 -translate-x-1/2 -translate-y-1/2 rounded-full border-[3px] border-background bg-foreground ring-1 ring-border"
                style={{ left: `${tradeoff}%` }}
              />
            </div>
          </div>

          <button
            type="button"
            onClick={previewRoute}
            disabled={!prompt.trim() || isPreviewing}
            className="mt-4 inline-flex w-full items-center justify-center gap-2 rounded-full bg-foreground px-5 py-3 text-[12px] font-medium uppercase tracking-[0.08em] text-background transition-opacity hover:opacity-90 disabled:cursor-not-allowed disabled:opacity-40"
          >
            {isPreviewing && <Loader2 className="h-4 w-4 animate-spin" />}
            {isPreviewing ? 'Ranking models' : 'Rank models'}
          </button>

          {error && <p className="mt-4 text-small leading-5 text-red-500">{error}</p>}
        </div>

        <div className="p-5 sm:p-7">
          <div className="flex items-center justify-between gap-4">
            <span className="font-mono text-[11px] uppercase tracking-[0.18em] text-foreground-muted">Ranked models</span>
            {embeddedSelected && <span className="text-small text-brand-mint">★ selected</span>}
          </div>

          {embeddedCandidates.length ? (
            <div className="mt-4 divide-y divide-border border-y border-border">
              {embeddedCandidates.map(candidate => {
                const isSelected = embeddedSelected?.model === candidate.model;
                return (
                  <div
                    key={candidate.model}
                    className={`grid grid-cols-[32px_minmax(0,1fr)] gap-3 px-1 py-3.5 ${isSelected ? 'bg-brand-mint/[0.07]' : ''}`}
                  >
                    <span className={`font-mono text-[12px] ${isSelected ? 'text-brand-mint' : 'text-foreground-muted'}`}>
                      #{candidate.rank}
                    </span>
                    <div className="flex min-w-0 flex-wrap items-baseline justify-between gap-x-4 gap-y-1">
                      <span className={`min-w-0 font-body text-[15px] ${isSelected ? 'text-foreground' : 'text-foreground-secondary'}`}>
                        {shortModelName(candidate.model)}
                      </span>
                      <span className="flex gap-4 font-mono text-[11px] text-foreground-muted">
                        <span>score <strong className="font-medium text-foreground-secondary">{formatScore(candidate.predictedScore)}</strong></span>
                        <span>est. /1k <strong className="font-medium text-foreground-secondary">{formatUsd(candidate.estimatedCostUsd * 1000)}</strong></span>
                      </span>
                    </div>
                  </div>
                );
              })}
            </div>
          ) : (
            <div className="mt-4 rounded-2xl border border-dashed border-border px-5 py-8 text-center text-small text-foreground-muted">
              Enter a request to rank the model pool.
            </div>
          )}
        </div>
      </section>
    );
  }

  return (
    <div className="min-h-screen bg-background text-foreground">
      <Header />
      <ThemeToggle />

      <main className="pb-24 pt-28 sm:pt-32">
        <div className="mx-auto w-full max-w-[1320px] px-4 sm:px-6 md:px-10">
          <section className="grid gap-7 border-b border-border pb-10 md:grid-cols-[minmax(0,1.05fr)_minmax(320px,0.75fr)] md:items-end">
            <div>
              <div className="mb-5 flex flex-wrap items-center gap-3">
                <span className="text-label-sm text-foreground-muted">Router Lab</span>
                <StatusPill health={health} />
              </div>
              <h1 className="max-w-4xl font-display text-[40px] leading-[43px] tracking-normal text-foreground sm:text-[48px] sm:leading-[51px] md:text-[58px] md:leading-[61px]">
                Which model should answer this request?
              </h1>
            </div>
            <div className="md:pb-1">
              <p className="max-w-xl text-body-lg leading-8 text-foreground-secondary">
                This is the router behind our winning RouterArena submission. The leaderboard saw one cost setting; here, you can move the objective and watch the same outcome predictions reorder the model pool.
              </p>
              <a
                href="/blog/how-we-accidentally-trained-the-1-model-router"
                className="mt-5 inline-flex border-b border-foreground/25 pb-0.5 text-body-sm text-foreground transition-colors hover:border-foreground"
              >
                How we trained it
              </a>
            </div>
          </section>

          <section className="mt-7 overflow-hidden rounded-[26px] border border-border bg-foreground/[0.02] dark:bg-white/[0.02]">
            <div className="grid xl:grid-cols-[minmax(0,1.2fr)_minmax(360px,0.8fr)]">
              <div className="border-b border-border p-5 sm:p-7 xl:border-b-0 xl:border-r">
                <div className="flex items-center justify-between gap-4">
                  <label htmlFor="router-lab-prompt" className="font-mono text-[12px] uppercase tracking-[0.16em] text-foreground-muted">
                    Request
                  </label>
                  <span className="font-mono text-[11px] text-foreground-muted">{prompt.length}/{promptLimit}</span>
                </div>
                <textarea
                  id="router-lab-prompt"
                  value={prompt}
                  onChange={event => {
                    setPrompt(event.target.value);
                    resetRouteState();
                  }}
                  maxLength={promptLimit}
                  className="mt-4 min-h-[178px] w-full resize-y border-0 bg-transparent p-0 font-body text-[18px] leading-8 text-foreground outline-none placeholder:text-foreground-muted sm:min-h-[150px]"
                  placeholder="Give the router one request."
                />
                <div className="mt-4 flex flex-col gap-4 border-t border-border pt-4 sm:flex-row sm:items-center sm:justify-between">
                  <div className="flex flex-wrap gap-2">
                    {SAMPLE_PROMPTS.map(sample => (
                      <button
                        key={sample.label}
                        type="button"
                        onClick={() => {
                          setPrompt(sample.text);
                          resetRouteState();
                        }}
                        className="rounded-full border border-border px-3 py-1.5 text-small text-foreground-secondary transition-colors hover:border-foreground/30 hover:text-foreground"
                      >
                        {sample.label}
                      </button>
                    ))}
                  </div>
                  <button
                    type="button"
                    onClick={() => {
                      setPrompt(DEFAULT_PROMPT);
                      setTradeoff(50);
                      resetRouteState();
                    }}
                    className="shrink-0 text-small text-foreground-muted underline decoration-foreground/20 underline-offset-4 transition-colors hover:text-foreground"
                  >
                    Reset example
                  </button>
                </div>
              </div>

              <div className="flex flex-col justify-between p-5 sm:p-7">
                <div>
                  <div className="flex items-center justify-between gap-4">
                    <span className="font-mono text-[12px] uppercase tracking-[0.16em] text-foreground-muted">Objective</span>
                    <span className="font-mono text-[11px] text-foreground-muted">{tradeoff}% quality</span>
                  </div>
                  <div className="relative mt-5 h-9">
                    <div className="absolute inset-x-0 top-1/2 h-1 -translate-y-1/2 rounded-full bg-foreground/15" />
                    <div
                      className="absolute left-0 top-1/2 h-1 -translate-y-1/2 rounded-full bg-brand-amber transition-[width] duration-150"
                      style={{ width: `${tradeoff}%` }}
                    />
                    <input
                      type="range"
                      min={0}
                      max={100}
                      step={1}
                      value={tradeoff}
                      onChange={event => setTradeoff(Number(event.target.value))}
                      aria-label="Trade off predicted accuracy against estimated cost"
                      aria-valuetext={`${tradeoff}% toward predicted accuracy`}
                      className="absolute inset-0 z-20 h-9 w-full cursor-pointer opacity-0"
                    />
                    <span
                      className="pointer-events-none absolute top-1/2 h-6 w-6 -translate-x-1/2 -translate-y-1/2 rounded-full border-[4px] border-background bg-brand-amber shadow-[0_10px_24px_-14px_rgba(0,0,0,0.9)] ring-1 ring-brand-amber/30 transition-[left] duration-150"
                      style={{ left: `${tradeoff}%` }}
                    />
                  </div>
                  <div className="mt-1 flex justify-between font-mono text-[11px] uppercase tracking-[0.12em] text-foreground-muted">
                    <span>Lower cost</span>
                    <span>Higher quality</span>
                  </div>
                  <p className="mt-5 text-body-sm leading-6 text-foreground-secondary">
                    Once the outcomes are predicted, this control only reranks them. It does not call the router again.
                  </p>
                </div>

                <button
                  type="button"
                  onClick={previewRoute}
                  disabled={!prompt.trim() || isPreviewing || isRunning}
                  className="mt-6 inline-flex w-full items-center justify-center gap-2 rounded-full bg-foreground px-5 py-3.5 text-[12px] font-medium uppercase tracking-[0.08em] text-background transition-opacity hover:opacity-90 disabled:cursor-not-allowed disabled:opacity-40"
                >
                  {isPreviewing && <Loader2 className="h-4 w-4 animate-spin" />}
                  {isPreviewing ? 'Predicting outcomes' : hasScored ? 'Refresh predictions' : 'Predict outcomes'}
                </button>
              </div>
            </div>
          </section>

          {(error || runStage) && (
            <section
              className={[
                'mt-4 rounded-2xl border px-5 py-4 text-body-sm',
                error
                  ? 'border-red-500/30 bg-red-500/10 text-foreground-secondary'
                  : 'border-border bg-foreground/[0.02] text-foreground-secondary dark:bg-white/[0.02]',
              ].join(' ')}
            >
              <div className="flex items-center gap-2">
                {!error && <Loader2 className="h-3.5 w-3.5 animate-spin" />}
                <span>{error || runStage}</span>
              </div>
              {!error && showDelayHint && (
                <p className="mt-2 text-small text-foreground-tertiary">This prediction is taking longer than usual.</p>
              )}
            </section>
          )}

          <section className="mt-7 grid gap-6 lg:grid-cols-[minmax(0,1.45fr)_minmax(320px,0.72fr)]">
            <div className="overflow-hidden rounded-[26px] border border-border bg-foreground/[0.02] dark:bg-white/[0.02]">
              <div className="flex flex-wrap items-start justify-between gap-4 border-b border-border px-5 py-5 sm:px-7">
                <div>
                  <p className="font-mono text-[12px] uppercase tracking-[0.16em] text-foreground-muted">Outcome plane</p>
                  <p className="mt-2 text-body-sm text-foreground-secondary">Each point is the router's prediction for one model on this request.</p>
                </div>
                {hasScored && (
                  <div className="flex items-start sm:items-end">
                    <div
                      className="flex flex-wrap items-center gap-x-4 gap-y-2 font-mono text-[10px] uppercase tracking-[0.1em] text-foreground-muted"
                      aria-label="Outcome plane legend"
                    >
                    {selected && (
                      <span className="inline-flex items-center gap-1.5 text-brand-amber">
                        <span aria-hidden="true" className="text-[16px] leading-none">★</span>
                        <span>Current choice</span>
                      </span>
                    )}
                      <span>Uncertainty</span>
                      <span className="inline-flex items-center gap-1.5">
                        <svg width="25" height="13" viewBox="0 0 25 13" aria-hidden="true">
                          <ellipse cx="12.5" cy="6.5" rx="10.5" ry="4.5" className="fill-brand-pink stroke-brand-pink" fillOpacity="0.12" strokeOpacity="0.55" />
                        </svg>
                        Run-to-run variation
                      </span>
                      <span className="inline-flex items-center gap-1.5">
                        <svg width="25" height="13" viewBox="0 0 25 13" aria-hidden="true">
                          <ellipse cx="12.5" cy="6.5" rx="10.5" ry="4.5" fill="none" className="stroke-brand-pink" strokeOpacity="0.7" strokeDasharray="2 3" />
                        </svg>
                        Router uncertainty
                      </span>
                    </div>
                  </div>
                )}
              </div>

              <div className="p-3 sm:p-5">
                <CandidatePlane
                  candidates={candidates}
                  selected={selected}
                  activeModel={activeModel}
                  tradeoff={tradeoff}
                  ready={hasScored}
                  onActiveModel={setActiveModel}
                />
              </div>

              <div className="border-t border-border p-5 sm:p-7">
                {selected ? (
                  <div className="grid gap-5 md:grid-cols-[minmax(0,1fr)_auto] md:items-end">
                    <div>
                      <p className="font-mono text-[11px] uppercase tracking-[0.16em] text-foreground-muted">Selected model</p>
                      <h2 className="mt-2 font-display text-[30px] leading-[35px] tracking-normal text-foreground sm:text-[34px] sm:leading-[39px]">
                        {selected.model}
                      </h2>
                      <p className="mt-3 max-w-2xl text-body-sm leading-6 text-foreground-secondary">
                        At this setting, no other model offers a better predicted tradeoff between accuracy and cost.
                      </p>
                    </div>
                    <div className="flex flex-wrap gap-x-7 gap-y-3 md:justify-end">
                      <div>
                        <div className="font-mono text-[10px] uppercase tracking-[0.14em] text-foreground-muted">Accuracy</div>
                        <div className="mt-1 font-mono text-[18px] text-foreground">{formatScore(selected.predictedScore)}</div>
                      </div>
                      <div>
                        <div className="font-mono text-[10px] uppercase tracking-[0.14em] text-foreground-muted">Est. / 1k</div>
                        <div className="mt-1 font-mono text-[18px] text-foreground">{formatUsd(selected.estimatedCostUsd * 1000)}</div>
                      </div>
                    </div>
                  </div>
                ) : (
                  <p className="text-body text-foreground-secondary">The model choice will appear as soon as the prediction returns.</p>
                )}
              </div>
            </div>

            <aside className="rounded-[26px] border border-border bg-foreground/[0.02] p-5 dark:bg-white/[0.02] sm:p-6">
              <div className="mb-5 border-b border-border pb-5">
                <p className="font-mono text-[12px] uppercase tracking-[0.16em] text-foreground-muted">Model order</p>
                <p className="mt-2 text-body-sm leading-6 text-foreground-secondary">
                  Move the objective and watch the same predictions reorder.
                </p>
              </div>
              <AnimatedRanking
                candidates={candidates}
                selected={selected}
                ready={hasScored}
                activeModel={activeModel}
                onActiveModel={setActiveModel}
              />

              <div className="mt-5 border-t border-border pt-5">
                <button
                  type="button"
                  onClick={runRouter}
                  disabled={!hasScored || isRunning || isPreviewing || health?.runsEnabled === false}
                  className="inline-flex w-full items-center justify-center gap-2 rounded-full border border-border px-5 py-3 text-[12px] font-medium uppercase tracking-[0.08em] text-foreground transition-colors hover:border-foreground/35 disabled:cursor-not-allowed disabled:opacity-40"
                >
                  {isRunning && <Loader2 className="h-4 w-4 animate-spin" />}
                  {isRunning ? 'Generating' : 'Generate response'}
                </button>
              </div>
            </aside>
          </section>

          {showGenerationPanel && (
            <section className="mt-8 border-t border-border pt-5 sm:mt-10 sm:pt-6" aria-live="polite">
              <div className="grid grid-cols-[minmax(0,1fr)_auto] items-start gap-3">
                <div className="min-w-0 sm:flex sm:items-center sm:gap-3">
                  <h2 className="font-mono text-[11px] uppercase tracking-[0.16em] text-foreground-muted">
                    Model response
                  </h2>
                  {hasRun && result?.selected && (
                    <div className="mt-2 flex min-w-0 items-center gap-3 sm:mt-0">
                      <span className="h-1 w-1 rounded-full bg-foreground-muted/50" aria-hidden="true" />
                      <span className="min-w-0 truncate text-body-sm text-foreground-secondary">{result.selected.model}</span>
                    </div>
                  )}
                </div>
                <button
                  type="button"
                  onClick={copyResponse}
                  disabled={!hasRun || !answer}
                  className="inline-flex items-center gap-2 px-1 py-1 text-[12px] font-medium text-foreground-secondary transition-colors hover:text-foreground disabled:cursor-not-allowed disabled:opacity-40"
                  title="Copy response"
                >
                  {copied ? <Check className="h-4 w-4" /> : <Copy className="h-4 w-4" />}
                  <span>{copied ? 'Copied' : 'Copy'}</span>
                </button>
              </div>
              <div className="mt-6 min-h-[150px] border-b border-border pb-8 sm:mt-8 sm:pb-10">
                {hasRun && answer ? (
                  <FormattedResponse text={answer} />
                ) : (
                  <p className="inline-flex items-center gap-2 text-body text-foreground-secondary">
                    <Loader2 className="h-4 w-4 animate-spin" />
                    Generating response.
                  </p>
                )}
              </div>
              {hasRun && result?.dryRun && (
                <p className="mt-4 text-small text-foreground-muted">Live provider calls are disabled in this local test mode.</p>
              )}
            </section>
          )}
        </div>
      </main>

      <Footer />
    </div>
  );
}

export function RouterPlaygroundPage() {
  return (
    <ThemeProvider defaultTheme="light">
      <RouterPlaygroundContent />
    </ThemeProvider>
  );
}

export function RouterPlaygroundEmbed() {
  return <RouterPlaygroundContent embedded />;
}

export default RouterPlaygroundPage;
